import { getConfig } from '../constants/constants';

/**
 * Secure logging utility that prevents sensitive data from being logged in production
 * and provides different log levels for better debugging
 */
class SecureLogger {
    private config = getConfig();
    private isDevelopment = this.config.ENABLE_LOGGING;

    /**
     * Sanitize data to remove sensitive information
     */
    public sanitizeData(data: any): any {
        if (typeof data === 'string') {
            // Remove potential sensitive patterns
            return data
                .replace(/password['":\s]*['"]\w+['"]/gi, 'password: "[REDACTED]"')
                .replace(/token['":\s]*['"]\w+['"]/gi, 'token: "[REDACTED]"')
                .replace(/key['":\s]*['"]\w+['"]/gi, 'key: "[REDACTED]"')
                .replace(/secret['":\s]*['"]\w+['"]/gi, 'secret: "[REDACTED]"')
                .replace(/email['":\s]*['"]\w+@[\w.]+['"]/gi, 'email: "[REDACTED]"');
        }
        
        if (typeof data === 'object' && data !== null) {
            const sanitized = { ...data };
            Object.keys(sanitized).forEach(key => {
                const lowerKey = key.toLowerCase();
                if (lowerKey.includes('password') || 
                    lowerKey.includes('token') || 
                    lowerKey.includes('secret') || 
                    lowerKey.includes('key') ||
                    lowerKey.includes('email')) {
                    sanitized[key] = '[REDACTED]';
                }
            });
            return sanitized;
        }
        
        return data;
    }

    /**
     * Format log message with timestamp and context
     */
    private formatMessage(level: string, message: string, context?: string): string {
        const timestamp = new Date().toISOString();
        const contextStr = context ? ` [${context}]` : '';
        return `[${timestamp}] ${level.toUpperCase()}${contextStr}: ${message}`;
    }

    /**
     * Log debug information (only in development)
     */
    debug(message: string, data?: any, context?: string): void {
        if (this.isDevelopment) {
            const formattedMessage = this.formatMessage('debug', message, context);
            if (data !== undefined) {
                console.log(formattedMessage, this.sanitizeData(data));
            } else {
                console.log(formattedMessage);
            }
        }
    }

    /**
     * Log general information
     */
    info(message: string, data?: any, context?: string): void {
        if (this.isDevelopment) {
            const formattedMessage = this.formatMessage('info', message, context);
            if (data !== undefined) {
                console.info(formattedMessage, this.sanitizeData(data));
            } else {
                console.info(formattedMessage);
            }
        }
    }

    /**
     * Log warnings (always logged)
     */
    warn(message: string, data?: any, context?: string): void {
        const formattedMessage = this.formatMessage('warn', message, context);
        if (data !== undefined) {
            console.warn(formattedMessage, this.sanitizeData(data));
        } else {
            console.warn(formattedMessage);
        }
    }

    /**
     * Log errors (always logged, but sanitized)
     */
    error(message: string, error?: Error | any, context?: string): void {
        const formattedMessage = this.formatMessage('error', message, context);
        if (error !== undefined) {
            if (error instanceof Error) {
                console.error(formattedMessage, {
                    name: error.name,
                    message: error.message,
                    stack: this.isDevelopment ? error.stack : '[REDACTED]'
                });
            } else {
                console.error(formattedMessage, this.sanitizeData(error));
            }
        } else {
            console.error(formattedMessage);
        }
    }

    /**
     * Log function entry/exit for debugging (development only)
     */
    trace(functionName: string, params?: any, context?: string): void {
        if (this.isDevelopment) {
            const formattedMessage = this.formatMessage('trace', `Entering ${functionName}`, context);
            if (params !== undefined) {
                console.trace(formattedMessage, this.sanitizeData(params));
            } else {
                console.trace(formattedMessage);
            }
        }
    }

    /**
     * Log API calls and database operations with sanitized parameters
     */
    api(operation: string, params?: any, context?: string): void {
        if (this.isDevelopment) {
            const sanitizedParams = params ? this.sanitizeData(params) : undefined;
            this.debug(`API Operation: ${operation}`, sanitizedParams, context || 'API');
        }
    }

    /**
     * Log authentication events (always logged but heavily sanitized)
     */
    auth(event: string, userId?: string, context?: string): void {
        const sanitizedUserId = userId ? `user_${userId.substring(0, 6)}***` : undefined;
        this.info(`Auth Event: ${event}`, { userId: sanitizedUserId }, context || 'AUTH');
    }
}

// Export singleton instance
export const logger = new SecureLogger();

// Legacy console.log replacement for gradual migration
export const safeLog = {
    log: (message: string, data?: any) => logger.info(message, data),
    debug: (message: string, data?: any) => logger.debug(message, data),
    info: (message: string, data?: any) => logger.info(message, data),
    warn: (message: string, data?: any) => logger.warn(message, data),
    error: (message: string, error?: any) => logger.error(message, error),
    trace: (functionName: string, params?: any) => logger.trace(functionName, params),
};
